import { useCallback, useEffect, useState } from 'react';

export const FUNNEL_PRICE_CATALOG_MAX_AGE_MS = 10 * 60 * 1000;
export const FUNNEL_PRICE_FALLBACKS = {
  blueprintCents: 4700,
  quickStartKitCents: 2700,
} as const;

export type FunnelPriceSource = 'live' | 'stale' | 'fallback' | 'unavailable';

export interface FunnelPriceSnapshot {
  source: FunnelPriceSource;
  blueprintCents: number;
  quickStartKitCents: number;
  generatedAt: string | null;
  canCheckout: boolean;
}

export interface FunnelPriceCatalogHook extends FunnelPriceSnapshot {
  refresh: () => Promise<FunnelPriceSnapshot>;
}

interface CatalogResponse {
  version: 1;
  generated_at: string;
  prices: {
    blueprint: { sku_key: 'audit_lto'; amount_cents: number };
    quick_start_kit: { sku_key: 'bump_quick_start_kit'; amount_cents: number };
  };
}

let cachedCatalog: CatalogResponse | null = null;
let inFlight: Promise<FunnelPriceSnapshot> | null = null;

export function formatUsd(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

export function getFunnelPriceSnapshot(now = Date.now()): FunnelPriceSnapshot {
  if (!cachedCatalog) return fallbackSnapshot('fallback');
  return snapshotFromCatalog(cachedCatalog, now);
}

export function loadFunnelPriceCatalog(): Promise<FunnelPriceSnapshot> {
  if (inFlight) return inFlight;
  inFlight = fetch('/api/funnel-prices', { headers: { Accept: 'application/json' } })
    .then(async (response) => {
      if (!response.ok) throw new Error('funnel_prices_unavailable');
      const catalog = parseCatalog(await response.json());
      cachedCatalog = catalog;
      return snapshotFromCatalog(catalog, Date.now());
    })
    .catch((error) => {
      const snapshot = cachedCatalog
        ? snapshotFromCatalog(cachedCatalog, Date.now())
        : fallbackSnapshot(error instanceof Error && error.message === 'funnel_prices_unavailable' ? 'unavailable' : 'fallback');
      return snapshot;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

export function useFunnelPriceCatalog(): FunnelPriceCatalogHook {
  const [snapshot, setSnapshot] = useState(() => getFunnelPriceSnapshot());
  const refresh = useCallback(async () => {
    const next = await loadFunnelPriceCatalog();
    setSnapshot(next);
    return next;
  }, []);

  useEffect(() => {
    let active = true;
    void loadFunnelPriceCatalog().then((next) => {
      if (active) setSnapshot(next);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (snapshot.source !== 'live' || !snapshot.generatedAt) return undefined;
    const expiresAt = Date.parse(snapshot.generatedAt) + FUNNEL_PRICE_CATALOG_MAX_AGE_MS;
    if (!Number.isFinite(expiresAt)) return undefined;
    // Reclassify an otherwise idle page exactly when its accepted catalog
    // reaches the ten-minute bound; checkout must not remain enabled merely
    // because no interaction triggered another render.
    const timer = setTimeout(() => setSnapshot(getFunnelPriceSnapshot()), Math.max(0, expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [snapshot.generatedAt, snapshot.source]);

  return { ...snapshot, refresh };
}

export function resetFunnelPriceCatalogForTests(): void {
  cachedCatalog = null;
  inFlight = null;
}

function parseCatalog(value: unknown): CatalogResponse {
  if (!value || typeof value !== 'object') throw new Error('invalid_catalog');
  const candidate = value as Partial<CatalogResponse>;
  if (candidate.version !== 1 || typeof candidate.generated_at !== 'string' || !Number.isFinite(Date.parse(candidate.generated_at))) {
    throw new Error('invalid_catalog');
  }
  const prices = candidate.prices;
  if (!prices || !isPrice(prices.blueprint, 'audit_lto') || !isPrice(prices.quick_start_kit, 'bump_quick_start_kit')) {
    throw new Error('invalid_catalog');
  }
  return candidate as CatalogResponse;
}

function isPrice(value: unknown, skuKey: string): value is { sku_key: 'audit_lto' | 'bump_quick_start_kit'; amount_cents: number } {
  if (!value || typeof value !== 'object') return false;
  const price = value as { sku_key?: unknown; amount_cents?: unknown };
  return price.sku_key === skuKey && typeof price.amount_cents === 'number' && Number.isInteger(price.amount_cents) && price.amount_cents > 0;
}

function snapshotFromCatalog(catalog: CatalogResponse, now: number): FunnelPriceSnapshot {
  const generatedTime = Date.parse(catalog.generated_at);
  const live = Number.isFinite(generatedTime) && now >= generatedTime && now - generatedTime < FUNNEL_PRICE_CATALOG_MAX_AGE_MS;
  return {
    source: live ? 'live' : 'stale',
    blueprintCents: catalog.prices.blueprint.amount_cents,
    quickStartKitCents: catalog.prices.quick_start_kit.amount_cents,
    generatedAt: catalog.generated_at,
    canCheckout: live,
  };
}

function fallbackSnapshot(source: 'fallback' | 'unavailable'): FunnelPriceSnapshot {
  return {
    source,
    ...FUNNEL_PRICE_FALLBACKS,
    generatedAt: null,
    canCheckout: false,
  };
}
